import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import {
  CreateUserDocumentRequest,
  ProcessingFeeOverride,
  SearchUserEquipmentRequest,
  UpdateInsuranceDocStatusRequest,
  User,
  UserAddress,
  UserAddressCreateRequest,
  UserAddressSearchRequest,
  UserDocument,
  UserDriverLic,
  UserDriverLicRequest,
  UserEquipment,
  UserEquipmentRequest,
  UserInternalNote,
  UserInternalNoteRequest,
  UserSearchRequest,
  UserUpdateDamageWaiverStatusRequest,
} from './user';
import { handleRequest } from '../quipli-service-client';
import { UserParsers } from './user-parsers';
import { Pagination } from '../pagination/pagination';
import { PaginationParsers } from '../pagination/pagination-parsers';
import { OnAccountParsers } from '../payment/on-account/on-account-parsers';
import { OnAccount } from '../payment/on-account/on-account';
import { CustomerParsers } from '../../customer-api/customer/customer-parsers';
import { InsuranceDocument, InsuranceDocumentRequest } from '../../customer-api/customer/customer-customer';
import { CycleBillingTerms } from '../order/order';
import { UpsertRepresentativeRequest } from '../representatives/representative';

export const useUserAPI = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  // Users
  async function createUser(
    name: string,
    withoutEmail: boolean,
    structureType: string,
    email?: string,
    company?: string,
    phone?: string,
    representatives?: Omit<UpsertRepresentativeRequest, 'userId'>[],
    processingFeeOverride?: ProcessingFeeOverride,
  ): Promise<User> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        name,
        withoutEmail,
        email: email ? email.trim() : email,
        company,
        phone,
        type: structureType,
        representatives,
        processingFeeOverride,
      },
    };

    return UserParsers.toUser(await handleRequest<UserParsers.UserJson>(config));
  }

  async function deleteUser(userId: number): Promise<User> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<User>(config);
  }

  async function getUserById(userId: number): Promise<User> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return UserParsers.toUser(await handleRequest<UserParsers.UserJson>(config));
  }

  async function listUsers(): Promise<User[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<User[]>(config);
  }

  async function mergeUsers(activeUserId: number, mergedUserId: number) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/mergeUsers`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        activeUserId,
        mergedUserId,
      },
    };

    const result = await handleRequest<UserParsers.UserJson>(config);
    return UserParsers.toUser(result);
  }

  async function restoreUser(userId: number): Promise<User> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/restore`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return UserParsers.toUser(await handleRequest<UserParsers.UserJson>(config));
  }

  async function searchUsers(request: UserSearchRequest): Promise<Pagination<User>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<UserParsers.UserJson>>(config);
    return {
      pagination,
      items: UserParsers.toUsers(items),
    };
  }

  async function updateUser(update: User): Promise<User> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/${update.id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: UserParsers.toUserRequest(update),
    };

    return UserParsers.toUser(await handleRequest<UserParsers.UserJson>(config));
  }

  // UserAddresses
  async function createUserAddress(body: UserAddressCreateRequest): Promise<UserAddress> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/addresses`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleRequest<UserAddress>(config);
  }

  async function deleteUserAddress(addressId: number): Promise<User> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/users/addresses/${addressId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest(config);
  }

  async function getUserAddresses(userId: number, allowDeleted: boolean = false): Promise<UserAddress[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/addresses?allowDeleted=${allowDeleted}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<UserAddress[]>(config);
  }

  async function listUserAddressesByIds(ids: number[]): Promise<UserAddress[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/addresses/multiple`,
      headers: {
        'X-Tenant': tenant,
      },
      data: ids,
    };

    return await handleRequest<UserAddress[]>(config);
  }

  async function getUserAddress(userId: number, addressId: number): Promise<UserAddress> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/addresses/${addressId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<UserAddress>(config);
  }

  async function updateUserAddress(adddressId: number, body: UserAddressCreateRequest): Promise<UserAddress> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/addresses/${adddressId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleRequest<UserAddress>(config);
  }

  async function searchAddresses(req: UserAddressSearchRequest): Promise<Pagination<UserAddress>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/addresses/search`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return await handleRequest<PaginationParsers.PaginationJson<UserAddress>>(config);
  }

  // UserBankDetails / OnAccount
  async function generateUserOnAccount(userId: number): Promise<OnAccount> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/bankDetails`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<OnAccountParsers.OnAccountJson>(config);
    return OnAccountParsers.toOnAccount(result);
  }

  async function getUserBankDetail(userId: number): Promise<OnAccount | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/bankDetails`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<OnAccountParsers.OnAccountJson | undefined>(config);
    return result && OnAccountParsers.toOnAccount(result);
  }

  async function updateUserBankDetails(body: OnAccount): Promise<OnAccount> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/${body.userId}/bankDetails`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };

    const result = await handleRequest<OnAccountParsers.OnAccountJson>(config);
    return OnAccountParsers.toOnAccount(result);
  }

  // UserCycleBillingTerms
  async function getUserCycleBillingTerms(userId: number): Promise<CycleBillingTerms | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/cycleBillingTerms`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return UserParsers.toCycleBillingTerms(await handleRequest<UserParsers.CycleBillingTermsJson | undefined>(config));
  }

  async function updateUserCycleBillingTerms(userId: number, request: CycleBillingTerms): Promise<CycleBillingTerms> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/cycleBillingTerms`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        cycleLength: request.cycleLength.toISO(),
        paymentPeriod: request.paymentPeriod.toISO(),
        cycleInvoiceDay: request.cycleInvoiceDay,
        isDayOfMonth: request.isDayOfMonth,
      },
    };

    const result = await handleRequest<UserParsers.CycleBillingTermsJson>(config);
    return UserParsers.toCycleBillingTerms(result)!;
  }

  // UserDamageWaiver
  async function updateUserDamageWaiver(body: UserUpdateDamageWaiverStatusRequest): Promise<number> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/${body.userId}/updateDamageWaiver`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        hasDamageWaiverRequired: body.hasDamageWaiverRequired,
      },
    };
    return await handleRequest<number>(config);
  }

  // UserDocuments
  async function createUserDocument(request: CreateUserDocumentRequest): Promise<UserDocument> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${request.userId}/documents`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    return await handleRequest<UserDocument>(config);
  }

  async function createUpdateUserDocument(
    file: File,
    userId: string,
    userDocumentType: string,
    deleteExisting: boolean = false,
  ) {
    const data = new FormData();
    data.append('file', file);
    data.append('user_id', userId);
    data.append('user_document_type', userDocumentType);
    data.append('delete_existing_file', deleteExisting.toString());

    const config = {
      method: 'post',
      url: `${contextPath}legacy-app/manager/orders/uploadUserDocument`,
      headers: {
        'X-Tenant': tenant,
      },
      data: data,
      cache: false,
      contentType: false,
      processData: false,
      mimeType: 'multipart/form-data',
    };

    return await handleRequest<{ msg: string; fileId: number }>(config, true);
  }

  async function deleteUserDocument(documentId: number): Promise<string> {
    const data = new FormData();
    data.append('ufile_id', documentId.toString());

    const config = {
      method: 'post',
      url: `${contextPath}legacy-app/manager/users/delete_document`,
      headers: {
        'X-Tenant': tenant,
      },
      data: data,
    };

    return await handleRequest<string>(config, true);
  }

  async function downloadUserDocumentFile(userId: number, fileId: number) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/documents/${fileId}/download`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<string>(config);
  }

  async function getUserDocuments(userId: number): Promise<UserDocument[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/documents`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return handleRequest<UserDocument[]>(config);
  }

  // UserDriverLicense
  async function checkUserDriverLicenseUniqueness(userId: number): Promise<{ isValid: boolean; error: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/checkUserDriverLicenseUniqueness`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        userId,
      },
    };

    return await handleRequest<{ isValid: boolean; error: string }>(config);
  }

  async function createUserDriverLicense(
    userId: number,
    body: UserDriverLicRequest,
  ): Promise<{ isValid: boolean; error: string; owner?: { name: string; id: number; canMerge: boolean } }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/driverLicense`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleRequest<{
      isValid: boolean;
      error: string;
      ownerId?: { name: string; id: number; canMerge: boolean };
    }>(config);
  }

  async function deleteUserDriverLicense(userId: number): Promise<number> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/driverLicense`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<number>(config);
  }

  async function getUserDriverLicense(userId: number): Promise<UserDriverLic | undefined> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/driverLicense`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const response = await handleRequest<UserParsers.UserDriverLicenseJson>(config);

    return response && UserParsers.toUserDriverLicense(response);
  }

  async function updateUserDriverLicense(userId: number, body: UserDriverLicRequest): Promise<number> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/driverLicense`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleRequest<number>(config);
  }

  // UserEquipment
  async function createUserEquipment(request: UserEquipmentRequest): Promise<UserEquipment> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${request.userId}/equipment`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    return await handleRequest<UserEquipment>(config);
  }

  async function searchUserEquipment(request: SearchUserEquipmentRequest): Promise<Pagination<UserEquipment>> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${request.userId}/equipment/search?keyword=${request.keyword}&pageSize=${request.pageSize}&pageNumber=${request.pageNumber}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const { items, pagination } = await handleRequest<PaginationParsers.PaginationJson<UserParsers.UserEquipmentJson>>(
      config,
    );

    return {
      pagination,
      items: items.map(UserParsers.toUserEquipment),
    };
  }

  async function updateUserEquipment(id: string, request: UserEquipmentRequest): Promise<UserEquipment> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/${request.userId}/equipment/${id}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };

    return await handleRequest<UserEquipment>(config);
  }

  // UserInternalNotes
  async function createUserInternalNote(userId: number, body: UserInternalNoteRequest): Promise<UserInternalNote> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/internalNotes`,
      headers: {
        'X-Tenant': tenant,
      },
      data: body,
    };
    return await handleRequest<UserInternalNote>(config);
  }

  async function deleteUserInternalNote(userId: number, internalNoteId: number): Promise<number> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/internalNotes/${internalNoteId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<number>(config);
  }

  async function listUserInternalNotes(userId: number): Promise<UserInternalNote[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/internalNotes`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return handleRequest<UserInternalNote[]>(config);
  }

  // InsuranceDocuments
  async function addInsuranceDocument(userId: number, body: InsuranceDocumentRequest): Promise<InsuranceDocument> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/insurance`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...body, expiryDate: body.expiryDate?.toISODate(), issuedDate: body.issuedDate?.toISODate() },
    };
    return CustomerParsers.toCustomerInsurance(await handleRequest<CustomerParsers.CustomerInsuranceJson>(config));
  }

  async function deleteInsuranceDocument(userId: number, documentId: number): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/insurance/${documentId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<void>(config);
  }

  async function getInsuranceDocuments(userId: number): Promise<InsuranceDocument[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/insurance`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return (await handleRequest<CustomerParsers.CustomerInsuranceJson[]>(config)).map(
      CustomerParsers.toCustomerInsurance,
    );
  }

  async function updateDocumentInsuranceStatus(body: UpdateInsuranceDocStatusRequest): Promise<number> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/documents/${body.documentId}/updateStatus`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        userId: body.userId,
        documentStatus: body.documentStatus,
      },
    };
    return await handleRequest<number>(config);
  }

  async function updateInsuranceDocument(
    userId: number,
    documentId: number,
    body: InsuranceDocumentRequest,
  ): Promise<InsuranceDocument> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/insurance/${documentId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: { ...body, expiryDate: body.expiryDate?.toISODate(), issuedDate: body.issuedDate?.toISODate() },
    };
    return await handleRequest<InsuranceDocument>(config);
  }

  // Statements
  async function generateStatement(userId: number, req: { addressId?: number }): Promise<BlobPart> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/statement`,
      headers: {
        'X-Tenant': tenant,
      },
      data: req,
    };

    return await handleRequest(
      {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'application/pdf',
        },
        responseType: 'blob',
      },
      true,
    );
  }

  async function sendStatement(userId: number): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/yorent/v1/users/${userId}/statement/send`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest(config);
  }

  return {
    // Users
    createUser,
    deleteUser,
    getUserById,
    listUsers,
    mergeUsers,
    restoreUser,
    searchUsers,
    updateUser,
    // UserAddresses
    createUserAddress,
    deleteUserAddress,
    getUserAddresses,
    listUserAddressesByIds,
    getUserAddress,
    updateUserAddress,
    searchAddresses,
    // UserBankDetails
    generateUserOnAccount,
    getUserBankDetail,
    updateUserBankDetails,
    // UserCycleBillingTerms
    getUserCycleBillingTerms,
    updateUserCycleBillingTerms,
    // UserDamageWaiver
    updateUserDamageWaiver,
    // UserDocuments
    createUserDocument,
    createUpdateUserDocument,
    deleteUserDocument,
    downloadUserDocumentFile,
    getUserDocuments,
    // UserDriverLicense
    checkUserDriverLicenseUniqueness,
    createUserDriverLicense,
    deleteUserDriverLicense,
    getUserDriverLicense,
    updateUserDriverLicense,
    // UserEquipment
    createUserEquipment,
    searchUserEquipment,
    updateUserEquipment,
    // UserInternalNotes
    createUserInternalNote,
    deleteUserInternalNote,
    listUserInternalNotes,
    // InsuranceDocuments
    addInsuranceDocument,
    deleteInsuranceDocument,
    getInsuranceDocuments,
    updateDocumentInsuranceStatus,
    updateInsuranceDocument,
    // Statements
    generateStatement,
    sendStatement,
  };
};
